import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, observable, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IAccountSetting, IFilterCriteria } from './account-setting-interfaces';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import {
  NotFoundError,
  OperationFailedError,
} from 'src/app/shared/errors/errors';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import {
  ICardCredentials,
  IShareAccess,
  IUser,
  IUserProfile,
  IUserProfileAndAccess,
} from 'src/app/user-management/user-management-interfaces';
import {
  cardStatusResponse,
  userTypeResponse,
} from 'src/app/shared/constants/response-mapping';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { TranslateService } from '@ngx-translate/core';
import { getFullName } from 'src/app/shared/utils/full-name.util';
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  private url = environment.graphqlUrl;
  readonly CONSTANTS = CONSTANTS;
  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  getUserInfo(): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query { getMyInfo {firstName middleName lastName phoneNumber email faceId mFAEnabled, pushNotificationEnabled}}',
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyInfo) {
            const userInfo: IAccountSetting = res.data.getMyInfo;
            const user: IUser = this.stateManagementService.getUser();
            user.userId = userInfo.email;
            user.email = userInfo.email;
            user.firstName = userInfo.firstName;
            user.middleName = userInfo.middleName;
            user.lastName = userInfo.lastName;
            user.phoneNumber = userInfo.phoneNumber;
            user.faceId = userInfo.faceId;
            user.mFAEnabled = userInfo.mFAEnabled;
            user.pushNotificationEnabled = userInfo.pushNotificationEnabled;
            user.name = getFullName(userInfo.firstName, userInfo.middleName, userInfo.lastName);
            this.stateManagementService.updateUser(user);
            return userInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used get complete user profile.
   *   @returns return user object
   * */
  getUserProfileInfo(siteId: string): Observable<any> {
    const query = `query($siteId: String) {
      getUserInfo(siteId: $siteId) {
          firstName
          middleName
          lastName
          phoneNumber
          email
          faceId
          mFAEnabled
          isDeactivated
          userGroups
          isMobileCredentialEnabled
          isCardCredentialEnabled
          invitationPending
          autoDeactivationDate
          userType
          userAccess {
              homeAccess {
                  buildingName
                  entityName
                  entityType
                  floorName
                  startTime
                  unitName
                  deviceStatus
              }
              otherAccess {
                  buildingName
                  entityName
                  entityType
                  floorName
                  startTime 
                  deviceStatus
              }
              temporaryAccess {
                  buildingName
                  entityName
                   entityType
                   floorName
                  startTime
                   unitName
                   expireTime
                   repeatStartTime
                   repeatEndTime
                   repeatDays
                   isExpired
                   isStarted
                   deviceStatus
               }
          }
      }
  }`;
    let variables: any = {
      siteId,
    };
    return this.http
      .post<any>(`${this.url}`, {
        query,
        variables,
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUserInfo) {
            const userInfo: IUserProfile = this.normalizeUserDeactivationFields(
              res.data.getUserInfo
            );
            return userInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  getMyPersonalInfo(siteId: String): Observable<any> {
    return forkJoin({
      getMyInfo: this.getMyInfo(),
      getMyAccess: this.getMyAccess(siteId),
    }).pipe(
      map((result: any) => {
        if (result?.getMyInfo && result?.getMyAccess) {
          const userInfo: IUserProfile = {
            ...result?.getMyInfo,
            ...result?.getMyAccess,
          };
          return userInfo;
        } else if (result?.errors?.length > 0) {
          throw new Error(result?.errors[0]?.errorType);
        }
      }),
      catchError((err: any) => {
        switch (err.name) {
          case ERROR_CODES.FAILED:
            throw new NotFoundError(
              this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
            );
          default:
            throw new Error(
              this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
            );
        }
      })
    );
  }

  /**
   * This function is used get complete user profile.
   *   @returns return user object
   * */
  getMyInfo(): Observable<any> {
    const query = `query {
                    getMyInfo  {
                      email
                      faceId
                      firstName
                      lastName
                      middleName
                      isMobileCredentialEnabled,
                      pushNotificationEnabled
                      invitationPending
                      mFAEnabled
                      isDeactivated
                      phoneNumber
                      userType
                      userImgKey
                      userImgUrl
                      userImgName
                    }
                }`;
    return this.http
      .post<any>(`${this.url}`, {
        query,
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyInfo) {
            const userInfo: IUserProfile = res.data.getMyInfo;

            const user: IUser = this.stateManagementService.getUser();
            user.userId = userInfo.email!;
            user.email = userInfo.email;
            user.firstName = userInfo.firstName;
            user.lastName = userInfo.lastName;
            user.phoneNumber = userInfo.phoneNumber;
            user.faceId = userInfo.faceId;
            user.mFAEnabled = userInfo.mFAEnabled;
            user.userImgKey = userInfo.userImgKey;
            user.userImgUrl = userInfo.userImgUrl;
            user.userImgName = userInfo.userImgName;
            user.pushNotificationEnabled = userInfo.pushNotificationEnabled;
            user.name = userInfo.firstName + ' ' + userInfo.lastName;
            user.userType = userInfo.userType =
              userTypeResponse[Number(userInfo.userType)] ?? null;
            this.stateManagementService.updateUser(user);
            return userInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used get complete user profile.
   *   @returns return user object
   * */
  getMyAccess(siteId: String): Observable<any> {
    const query = `query ($siteId: String!) {
      getMyAccess(siteId: $siteId) {
        userGroups {
          userGroupName
          userGroupId
          startTime
          repeatStartTime
          repeatEndTime
          repeatDays
          expireTime
        }
        userAccess {
          homeAccess {
            accessId
            buildingName
            entityName
            entityType
            expireTime
            floorName
            repeatDays
            repeatEndTime
            repeatStartTime
            startTime
            unitName
            userGroup
          }
          otherAccess {
            accessId
            buildingName
            entityName
            entityType
            expireTime
            floorName
            repeatDays
            repeatEndTime
            repeatStartTime
            startTime
            unitName
            userGroup
          }
          temporaryAccess {
            userGroup
            unitName
            startTime
            repeatStartTime
            repeatEndTime
            repeatDays
            floorName
            expireTime
            entityType
            entityName
            buildingName
            accessId
          }
        }
        accessPointCount
        deviceCount
      }
    }
`;
    let variables: any = {
      siteId,
    };
    return this.http
      .post<any>(`${this.url}`, {
        query,
        variables,
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyAccess) {
            const userInfo: IUserProfile = res.data.getMyAccess;
            const commonAccessControl: boolean =
              this.stateManagementService.getSharedData()?.selectedSite
                ?.commonAccessControl;

            userInfo.userGroups?.forEach((userGroup: any) => {
              if (
                CONSTANTS.DEFAULT_USER_GROUPS.includes(userGroup.userGroupName)
              ) {
                userGroup.userGroupName = this.translate.instant(
                  userGroup.userGroupName
                );
              }
            });

            if (!commonAccessControl) {
              userInfo.userGroups = userInfo.userGroups?.filter(
                (userGroup: any) =>
                  this.translate
                    .instant('DEFAULT_USER_GROUPS')
                    .includes(userGroup.userGroupName)
              );
            }

            return userInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used get complete user profile.
   *   @returns return user object
   * */
  getSpecficUserProfileInfo(siteId: string, userId: string): Observable<any> {
    const query = `query($siteId: String!, $userId: String!) {
      getUserInfo(siteId: $siteId, userId: $userId) {
          userId
          stagedEmail
          firstName
          middleName
          lastName
          notes
          isTemporaryUser
          hasStandardCredential
         userImgUrl
          phoneNumber
          email
          faceId
          mFAEnabled
          isDeactivated
          userGroups {
              userGroupName
              userGroupId
              startTime
              expireTime
              repeatStartTime
              repeatEndTime
              repeatDays
          }
          isMobileCredentialEnabled
          isCardCredentialEnabled
          invitationPending
          autoDeactivationDate
          userType
          deviceCount
          accessPointCount
          userAccess {
              homeAccess {
                  buildingName
                  entityName
                  entityType
                  floorName
                  startTime
                  unitName
                  unitId
                  accessId
                  accessLevel
                 deviceStatus
                 activationState
                 batteryPercentage
                 batteryAlarm
              }
              otherAccess {
                  buildingName
                  entityName
                  entityType
                  floorName
                  startTime
                  unitId
                  accessId
                  accessLevel
                  deviceStatus
                  activationState
                  batteryPercentage
                  batteryAlarm

              }
              temporaryAccess {
                  buildingName
                  entityName
                   entityType
                   floorName
                  startTime
                   unitName
                   unitId
                   expireTime
                   repeatStartTime
                   repeatEndTime
                   repeatDays
                   accessId
                   accessLevel
                   isStarted
                   isExpired
                   deviceStatus
                  activationState
                  batteryPercentage
                  batteryAlarm
               }
          }
      }
  }
`;
    let variables: any = {
      siteId,
      userId,
    };
    return this.http
      .post<any>(`${this.url}`, {
        query,
        variables,
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUserInfo) {
            const userInfo = this.normalizeUserDeactivationFields<IUserProfileAndAccess>(
              res.data.getUserInfo
            );
            const commonAccessControl: boolean =
              this.stateManagementService.getSharedData()?.selectedSite
                ?.commonAccessControl;

            userInfo.userGroups?.forEach((userGroup: any) => {
              if (
                CONSTANTS.DEFAULT_USER_GROUPS.includes(userGroup.userGroupName)
              ) {
                userGroup.userGroupName = this.translate.instant(
                  userGroup.userGroupName
                );
              }
            });

            if (!commonAccessControl) {
              userInfo.userGroups = userInfo.userGroups?.filter(
                (userGroup: any) =>
                  this.translate
                    .instant('DEFAULT_USER_GROUPS')
                    .includes(userGroup.userGroupName)
              );
              userInfo.userAccess.otherAccess = [];
            }
            if (userTypeResponse.hasOwnProperty(userInfo.userType)) {
              userInfo.userTypeId = userInfo.userType;
              userInfo.userType = userTypeResponse[userInfo?.userType!];
            }
            console.log(userInfo);
            return userInfo;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_INFO_FAILED')
              );
          }
        })
      );
  }

  private normalizeUserDeactivationFields<T extends IUserProfile = IUserProfile>(userInfo: any): T {
    if (!userInfo) return userInfo;

    return {
      ...userInfo,
      isDeactivated: userInfo.isDeactivated ?? userInfo.IsDeactivated,
      autoDeactivationDate:
        userInfo.autoDeactivationDate ?? userInfo.AutoDeactivationDate,
    };
  }

  /**
   *
   * @param oldPassword
   * @param newPassword newPassword
   * @returns return server response result
   */
  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($newPassword: String!, $oldPassword: String!) { changePassword(newPassword: $newPassword, oldPassword: $oldPassword) { success }}',
        variables: {
          oldPassword: `${oldPassword}`,
          newPassword: `${newPassword}`,
        },
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.changePassword?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.ATTEMPT_LIMIT_EXCEEDED:
              throw new OperationFailedError(
                this.translate.instant(
                  'YOUR_PASSWORD_UPDATE_LIMIT_HAS_EXCEEDED'
                )
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
            case ERROR_CODES.INCORRECT_CURRENT_PASSWORD:
              throw new OperationFailedError(
                this.translate.instant('INCORRECT_CURRENT_PASSWORD')
              );
            case ERROR_CODES.BAD_ARGUMENT:
              throw new OperationFailedError(
                this.translate.instant('PASSWORD_DOES_NOT_MATCH')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
          }
        })
      );
  }

  /**
   * @description this method will update user
   * @param { boolean } faceId
   * @param { boolean } mFAEnabled
   * @param { boolean } pushNotificationEnabled
   * @returns { Observable<any> }
   */
  updateUser(
    faceId: boolean,
    mFAEnabled: boolean,
    pushNotificationEnabled: boolean
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($faceId: String!, $mFAEnabled: String!, $pushNotificationEnabled: String!) { updateUser(faceId: $faceId, mFAEnabled: $mFAEnabled, pushNotificationEnabled: $pushNotificationEnabled) { email  }}',
        variables: {
          faceId,
          mFAEnabled,
          pushNotificationEnabled,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.updateUser) {
            const result = res?.data?.updateUser;
            const user: IUser = this.stateManagementService.getUser();
            user.faceId = faceId;
            user.mFAEnabled = mFAEnabled;
            user.pushNotificationEnabled = pushNotificationEnabled;
            this.stateManagementService.updateUser(user);
            return result;
          } else if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.USER_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_USER_IS_AVAILABLE')
              );
            default:
              throw new Error(this.translate.instant('FAILED_TO_UPDATE_USER'));
          }
        })
      );
  }

  /**
   * @description this method will update user
   * @param { boolean } mobileCredential
   * @returns { Observable<any> }
   */
  updateUserCredentials(mobileCredential: boolean): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($isMobileCredentialEnabled: String!) { enableDisableMobileCredentials (isMobileCredentialEnabled: $isMobileCredentialEnabled) {  email  }  } ',
        variables: {
          isMobileCredentialEnabled: mobileCredential,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.enableDisableMobileCredentials) {
            const result = res?.data?.enableDisableMobileCredentials;
            return result;
          } else if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.USER_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_USER_IS_AVAILABLE')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATE_MOBILE_CREDENTIAL')
              );
          }
        })
      );
  }

  checkAccountDeactivationPermission(): Observable<{
    isDeactivationAllowed: boolean;
    blockingSites: string[];
  }> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query CheckAccountDeactivationPermission {
          checkAccountDeactivationPermission {
            isDeactivationAllowed
            blockingSites
          }
        }`,
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.checkAccountDeactivationPermission) {
            const result = res.data.checkAccountDeactivationPermission;
            return {
              isDeactivationAllowed: !!result.isDeactivationAllowed,
              blockingSites: result.blockingSites ?? [],
            };
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
          return {
            isDeactivationAllowed: true,
            blockingSites: [],
          };
        }),
        catchError(() => {
          throw new Error(
            this.translate.instant('FAILED_TO_DELETE_USER_ACCOUNT')
          );
        })
      );
  }

  /**
   * @description this method will delete user
   * @returns { Observable<any> }
   */
  deleteMyAccount(): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation { deleteMyAccount { userId }}`,
      })
      .pipe(
        map((res) => {
          if (res?.data?.deleteMyAccount) {
            const result = res?.data?.deleteMyAccount;
            return result;
          } else if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.USER_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_USER_IS_AVAILABLE')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_DELETE_USER_ACCOUNT')
              );
          }
        })
      );
  }

  /**
   * @description this method will delete user
   * @returns { Observable<any> }
   */
  deleteGroup(userGroupIds: string[], userId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation ($userId: String!, $userGroupIds: [String!]!) {  removeUserFromUserGroup(userGroupIds: $userGroupIds, userId: $userId) {    success  }}`,
        variables: {
          userGroupIds: userGroupIds,
          userId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.removeUserFromUserGroup) {
              return res?.data?.removeUserFromUserGroup;
            } else if (res?.errors?.length > 0) {
              let errorList = res?.errors.map((x: any) => x.message)[0];
              if (errorList.includes('User is not included in this group.')) {
                throw new Error(
                  this.translate.instant('USER_NOT_INCLUDED_IN_THIS_GROUP);')
                );
              }
              throw new Error(
                this.translate.instant('FAILED_TO_SHARE_ACCESS_SERVER_ERROR);')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method will update name of a user.
   * @param { string } firstName
   * @param { string } lastName
   * @returns { Observable<any> }
   */
  updateName(
    firstName: string,
    lastName: string,
    middleName?: string | null
  ): Observable<any> {
    const normalizedMiddleName = middleName?.trim() || null;
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($firstName: String, $middleName: String, $lastName: String) { updateUser(firstName: $firstName, middleName: $middleName, lastName: $lastName) { email }}',
        variables: {
          firstName,
          middleName: normalizedMiddleName,
          lastName,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.updateUser) {
            const user: IUser = this.stateManagementService.getUser();
            user.firstName = firstName;
            user.middleName = normalizedMiddleName;
            user.lastName = lastName;
            user.name = getFullName(firstName, normalizedMiddleName, lastName);
            this.stateManagementService.updateUser(user);
            const result = res?.data;
            return result;
          } else if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.INTERNAL_SERVER_ERROR:
              throw new NotFoundError(
                this.translate.instant('FAILED_TO_UPDATE_NAME')
              );
            default:
              throw new Error(this.translate.instant('FAILED_TO_UPDATE_NAME'));
          }
        })
      );
  }
  /**
   * @description this method will update name of a user.
   * @param { string } phoneNumber
   * @param { string } code
   * @returns { Observable<any> }
   */
  updatePhone(phoneNumber: string, code: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($code: String, $phoneNumber: String) { updateUser(phoneNumber: $phoneNumber, code: $code) { email} }',
        variables: {
          phoneNumber,
          code,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.updateUser) {
              return res?.data;
            } else if (res?.errors?.length > 0) {
              let errorList = res?.errors.map((x: any) => x.errorType)[0];
              if (errorList.includes(ERROR_CODES.PHONE_NUMBER_ALREADY_EXISTS)) {
                throw new Error(
                  this.translate.instant('PHONE_NUMBER_ALREADY_EXISTS')
                );
              } else if (errorList.includes(ERROR_CODES.CODE_EXPIRED)) {
                throw new Error(
                  this.translate.instant('VERIFICATION_CODE_IS_EXPIRE')
                );
              } else if (errorList.includes(ERROR_CODES.CODE_NOT_FOUND)) {
                throw new Error(
                  this.translate.instant('VERIFICATION_CODE_IS_INCORRECT')
                );
              }
              throw new Error(this.translate.instant('FAILED_TO_UPDATE_USER'));
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }
  /**
   *
   * @param address
   * @param codeType
   * @param email
   * @param medium
   * @returns return server response result
   */
  generateCode(
    address: string,
    codeType: string,
    medium: string
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($codeType: String!, $address: String!, $medium: String!) { generateCode(codeType: $codeType, address: $address, medium: $medium) { success }}',
        variables: {
          address: `${address}`,
          codeType: `${codeType}`,
          medium: `${medium}`,
        },
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.generateCode?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant(
                  'FAILED_TO_UPDATE_PHONE_NUMBER_TRY_AGAIN'
                )
              );
            case ERROR_CODES.BAD_ARGUMENT:
              throw new OperationFailedError(
                this.translate.instant('PHONE_NUMBER_ENTERED_IS_INCORRECT')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATED_PASSWORD_TRY_AGAIN')
              );
          }
        })
      );
  }

  /**
   * This function is used to generate a code for account deletion.
   * @param codeType
   * @param medium
   * @returns return server response result
   */
  generateDeleteCode(codeType: string, medium: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($codeType: String!, $medium: String!) { generateCode(codeType: $codeType, medium: $medium) { success }}',
        variables: {
          codeType: `${codeType}`,
          medium: `${medium}`,
        },
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.generateCode?.success) {
            return resData;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GENERATE_DELETE_CODE')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GENERATE_DELETE_CODE')
              );
          }
        })
      );
  }

  /**
   * This function is used to deactivate the user's own account.
   * @param code
   * @param medium
   * @returns return server response result
   */
  deactivateMyAccount(code: string, medium: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation DeactivateMyAccount($code: String!, $medium: String!) { deactivateMyAccount(code: $code, medium: $medium) { email }}',
        variables: {
          code: `${code}`,
          medium: `${medium}`,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.deactivateMyAccount) {
              return res?.data;
            } else if (res?.errors?.length > 0) {
              let errorList = res?.errors.map((x: any) => x.errorType)[0];
              if (errorList.includes(ERROR_CODES.CODE_EXPIRED)) {
                throw new Error(
                  this.translate.instant('VERIFICATION_CODE_IS_EXPIRE')
                );
              } else if (errorList.includes(ERROR_CODES.CODE_NOT_FOUND)) {
                throw new Error(
                  this.translate.instant('VERIFICATION_CODE_IS_INCORRECT')
                );
              } else if (
                errorList.includes(ERROR_CODES.NUMBER_OF_RETRIES_EXCEEDED)
              ) {
                throw new Error(
                  this.translate.instant('NUMBER_OF_RETRIES_EXCEEDED')
                );
              }
              throw new Error(
                this.translate.instant('FAILED_TO_DEACTIVATE_ACCOUNT')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * This function is used to getUserCardsList from the backend.
   *   @returns return user object
   * */
  getUserCardsList(email: string, siteId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query($email: String!, $siteId: String!) { getUserCardsList (email: $email, siteId: $siteId) {badgeId,credentialUser,cardEncoder,cardStatus,cardName, cardId, creatorName}}',
        variables: {
          email: `${email}`,
          siteId: `${siteId}`,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUserCardsList) {
            const updatedCards = res.data.getUserCardsList.map((card: any) => {
              const cardStatus =
                card.cardStatus &&
                this.translate.instant(cardStatusResponse[card.cardStatus]);
              return { ...card, cardStatus };
            });

            return updatedCards;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.CARDS_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_CARDS_ASSOCIATED_WITH_GIVEN_USER')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_CARDS_LIST_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used to getMyCardsList from the backend.
   *   @returns return user object
   * */
  getMyCardsList(email: string, siteId: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query MyQuery {  getMyCardsList {    badgeId    cardEncoder    cardId    cardName    cardStatus    credentialUser    creatorName  }}',
        variables: {
          email: `${email}`,
          siteId: `${siteId}`,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyCardsList) {
            const updatedCards = res?.data?.getMyCardsList?.map((card: any) => {
              if (card && card.hasOwnProperty('cardStatus')) {
                return {
                  ...card,
                  cardStatus: this.translate.instant(
                    cardStatusResponse[card.cardStatus]
                  ),
                };
              }
              return card;
            });
            return updatedCards;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.CARDS_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_CARDS_ASSOCIATED_WITH_GIVEN_USER')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_CARDS_LIST_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used to getUserAccessOnRFID from the backend.
   *   @returns return user object
   * */
  getUserAccessOnRFID(
    cardId: string,
    badgeId: string
  ): Observable<ICardCredentials> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query MyQuery($cardId: String! ,$badgeId: String!) {  getUserAccessOnRFID(cardId: $cardId, badgeId: $badgeId ) {    cardAccess { homeAccess {   accessId   buildingId   buildingName   entityName   entityType   expireTime   floorName unitName } otherAccess {   accessId   buildingId   buildingName   entityName   entityType   expireTime   floorName unitName } temporaryAccess {   accessId   buildingId   buildingName   entityName   entityType   expireTime   floorName unitName isExpired }    }    timezone  }}',
        variables: {
          cardId: `${cardId}`,
          badgeId: `${badgeId}`,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getUserAccessOnRFID) {
            return res?.data;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.CARD_NOT_FOUND:
              throw new NotFoundError(this.translate.instant('CARD_NOT_FOUND'));
            case ERROR_CODES.USER_ACCESS_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('USER_ACCESS_NOT_FOUND')
              );
            default:
              throw new Error(
                this.translate.instant(
                  'REQUEST_TO_GET_USER_CARD_DETAILS_FAILED'
                )
              );
          }
        })
      );
  }

  /**
   * This function is used to getMyEventHistory from the backend.
   *   @returns return user object
   * */
  getMyEventHistory(
    siteId: string,
    filterCriteria: IFilterCriteria
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query($siteId: String!, $filterCriteria: FilterCriteria!) {
                getMyEventHistory(filterCriteria: $filterCriteria, siteId: $siteId) {
                  events {
                    id,
                    eventCode,
                    eventDescription,
                    user,
                    timestamp,
                    isoTime,
                    deviceName,
                    unitName,
                    buildingName
                  }
                }
              }`,
        variables: {
          siteId,
          filterCriteria,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyEventHistory) {
            return res?.data?.getMyEventHistory?.events;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.EVENT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant(
                  'NO_ACTIVITY_AVAILABLE_FOR_THE_GIVEN_USER'
                )
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_ACTIVITY_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used to getEventHistoryByUser from the backend.
   *   @returns return user object
   * */
  getEventHistoryByUser(
    userId: string,
    filterCriteria: IFilterCriteria,
    siteId: string
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query($userId: String!, $filterCriteria: FilterCriteria!, $siteId: String!)  {
                getEventHistoryByUser(userId: $userId, filterCriteria: $filterCriteria, siteId:$siteId) {
                  events {
                    id,
                    eventCode,
                    eventDescription,
                    user,
                    timestamp,
                    isoTime,
                    deviceName,
                    unitName,
                    buildingName,
                    accessPointName,
                    eventTitle,
                    extraDescription,
                    source,
                    sourceName,
                    isIssue,
                    isAbnormal,
                    isMaster,
                    floorName,
                    wingName
                  }
                }
              }`,
        variables: { userId, filterCriteria, siteId },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getEventHistoryByUser) {
            return res?.data?.getEventHistoryByUser?.events;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.EVENT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant(
                  'NO_ACTIVITY_AVAILABLE_FOR_THE_GIVEN_USER'
                )
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_ACTIVITY_FAILED')
              );
          }
        })
      );
  }

  /**
   * This function is used to getEventHistoryByUser from the backend.
   *   @returns return user object
   * */
  getEventHistoryByUserNew(
    userId: string,
    filterCriteria: IFilterCriteria,
    siteId: string
  ): Observable<any> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `query($userId: String!, $filterCriteria: FilterCriteria!, $siteId: String!)  {
                getEventHistoryByUser(userId: $userId, filterCriteria: $filterCriteria, siteId:$siteId) {
                  events {
                    id,
                    eventCode,
                    eventDescription,
                    user,
                    timestamp,
                    isoTime,
                    deviceName,
                    unitName,
                    buildingName,
                    accessPointName,
                    eventTitle,
                    extraDescription,
                    source,
                    sourceName,
                    isIssue,
                    isAbnormal,
                    isMaster,
                    floorName,
                    wingName
                  },
                  totalRecords,
                  cursor
                }
              }`,
          variables: { userId, filterCriteria, siteId },
        },
        {
          headers: { loader: 'false' },
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getEventHistoryByUser) {
            return res?.data?.getEventHistoryByUser;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.EVENT_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant(
                  'NO_ACTIVITY_AVAILABLE_FOR_THE_GIVEN_USER'
                )
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_ACTIVITY_FAILED')
              );
          }
        })
      );
  }

  /**
   *
   * @param { string } accessId
   * @returns return server response result
   */
  removeUserAccess(accessId: string[]): Observable<any> {
    return this.executeRemoveUserAccess(
      'mutation ($accessId: [String!]!) { removeUserAccess (accessId: $accessId) { success }}',
      { accessId }
    );
  }

  /**
   * @description Removes a user from a unit using userId + unitId. Backend constructs
   *              the UserUnit access ID so this works even when the unit has no locks.
   * @param userId email/id of the user being removed from the unit
   * @param unitId unit to remove the user from
   */
  removeUserAccessFromUnit(userId: string, unitId: string): Observable<any> {
    return this.executeRemoveUserAccess(
      'mutation ($userId: String!, $unitId: String!) { removeUserAccess (userId: $userId, unitId: $unitId) { success }}',
      { userId, unitId }
    );
  }

  private executeRemoveUserAccess(
    query: string,
    variables: Record<string, unknown>
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query,
        variables,
      })
      .pipe(
        map((res) => {
          const resData = res?.data;
          if (resData?.removeUserAccess?.success) {
            return resData.removeUserAccess?.success;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            const graphQLError = res.errors[0];
            const error = new Error(graphQLError?.message);
            (error as Error & { errorType?: string }).errorType =
              graphQLError?.errorType;
            throw error;
          }
        }),
        catchError((err: any) => {
          switch (err.errorType || err.message) {
            case ERROR_CODES.USER_ACCESS_NOT_FOUND:
              throw new NotFoundError(err.message);
            case ERROR_CODES.INVALID_ACCESS_ID_WITH_GROUP:
              throw new OperationFailedError(
                this.translate.instant(
                  'ACCESS_SHARED_VIA_GROUP_CANNOT_BE_REMOVED_SEPARATELY'
                )
              );
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_REMOVE_USER_ACCESS')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_REMOVE_USER_ACCESS')
              );
          }
        })
      );
  }

  /**
   * @description this method gets presigned url for storing image to bucket
   * @returns { Observable<any> }
   */
  getSiteBucketPresignedURL(fileType: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation PresignedURL($uploadType: String!, $fileType: String!) {  presignedURL(uploadType: $uploadType, fileType: $fileType) { fields url imageName }}',
        variables: {
          uploadType: 'user',
          fileType: fileType,
        },
        operationName: 'PresignedURL',
      })
      .pipe(
        map((res) => {
          if (res?.data?.presignedURL) {
            let result = res?.data?.presignedURL;
            if (result?.fields) result.fields = JSON.parse(result.fields);
            return result;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
          }
        })
      );
  }

  /**
   * @description this method uploads image to S3 bucket with the provide pre-signed url
   * @param preSignedUrl
   * @param formData
   * @returns { Observable<any> }
   */
  uploadFile(preSignedUrl: any, formData: FormData): Observable<any> {
    return this.http.post<any>(`${preSignedUrl}`, formData);
  }

  /**
   * @description this method will update updateProfileImage
   * @param { boolean } userImgName
   * @param { boolean } userImgKey
   * @returns { Observable<any> }
   */
  updateProfileImage(
    userImgName: string,
    userImgKey: string,
    profileImageUrl: any
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($userImgName: String, $userImgKey: String) { updateUser(userImgName: $userImgName, userImgKey: $userImgKey) { email  }}',
        variables: {
          userImgName,
          userImgKey,
        },
      })
      .pipe(
        map((res) => {
          if (res?.data?.updateUser) {
            const user: IUser = this.stateManagementService.getUser();
            user.userImgUrl = profileImageUrl;
            user.userImgName = userImgName;
            user.userImgKey = userImgKey;
            this.stateManagementService.updateUser(user);
            return res?.data;
          } else if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.USER_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_USER_IS_AVAILABLE')
              );
            default:
              throw new Error(this.translate.instant('FAILED_TO_UPDATE_USER'));
          }
        })
      );
  }
  /*
   * @param { string } accessId
   * @returns return server response result
   */
  addUserGroupsToUserAccess(variables: any): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation ($expireTime: String, $repeatDays: [String], $repeatEndTime: String, $repeatStartTime: String, $siteId: String!, $startTime: String, $userGroupId: String!, $users: [UserProps!]!){
                addUsersToUserGroup (siteId: $siteId, startTime: $startTime, userGroupId: $userGroupId, users: $users, expireTime: $expireTime, repeatDays: $repeatDays, repeatEndTime: $repeatEndTime, repeatStartTime: $repeatStartTime) {
                  userGroupId
                }
              }`,
        variables,
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.addUsersToUserGroup) {
              return res?.data?.addUsersToUserGroup;
            } else if (res?.errors?.length > 0) {
              const error = res?.errors[0];
              if (
                error?.errorType === ERROR_CODES.USER_ACCOUNT_DELETION_PENDING
              ) {
                throw new Error(
                  this.translate.instant('USER_REQUESTED_ACCOUNT_DELETION')
                );
              }
              throw new Error(
                this.translate.instant('FAILED_TO_ADD_USER_TO_USER_GROUP')
              );
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to update user access
   * @param { IShareAccess } variables variables of type IShareAccess to give access object
   * @returns
   */
  updateUserAccess(variables: IShareAccess): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation ($accessId: String!, $expireTime: String, $startTime: String, $repeatStartTime: String, $repeatEndTime: String, $repeatDays: [String]){  updateUserAccess(accessId: $accessId, expireTime: $expireTime, startTime: $startTime, repeatStartTime: $ repeatStartTime, repeatEndTime: $repeatEndTime, repeatDays: $repeatDays) { success }}',
        variables,
      })
      .pipe(
        map((res) => {
          if (res?.data?.updateUserAccess?.success) {
            const result = res?.data?.updateUserAccess.success;
            return result;
          } else if (res.hasOwnProperty('errors') && res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.INVALID_ACCESS_ID_WITH_GROUP:
              throw new OperationFailedError(
                this.translate.instant(
                  'ACCESS_SHARED_VIA_GROUP_CANNOT_BE_UPDATAED_SEPARATELY'
                )
              );
            case ERROR_CODES.USER_ACCOUNT_DELETION_PENDING:
              throw new Error(
                this.translate.instant('USER_REQUESTED_ACCOUNT_DELETION')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATE_USER_ACCESS')
              );
          }
        })
      );
  }

  /**
   * @description Reads the release version the user last dismissed from their profile.
   * @returns Observable with dismissedReleaseVersion string, or null if unset
   */
  getDismissedReleaseVersion(): Observable<string | null> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query { getMyInfo { dismissedReleaseVersion } }',
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getMyInfo) {
            return res.data.getMyInfo.dismissedReleaseVersion ?? null;
          }
          if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
          return null;
        })
      );
  }

  /**
   * @description Persists the dismissed release-banner version on the user profile.
   * @param version App version string being dismissed
   */
  dismissReleaseVersion(version: string): Observable<boolean> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation($version: String!) { dismissReleaseVersion(version: $version) { success } }',
        variables: { version },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.dismissReleaseVersion) {
            return !!res.data.dismissReleaseVersion.success;
          }
          if (res?.errors?.length) {
            throw new Error(res?.errors[0]?.errorType);
          }
          return false;
        })
      );
  }
}
